import React, { useState } from 'react';
import { Share2, Facebook, Twitter, Linkedin, Link2, Check } from 'lucide-react';

interface SocialShareProps {
  title?: string;
  description?: string;
  url?: string;
  className?: string;
}

export default function SocialShare({
  title = 'ETWC Ministry - Evangelize the World Christian Ministry',
  description = 'Join ETWC Ministry in Cleveland, Ohio for powerful worship, biblical teaching, and community.',
  url = typeof window !== 'undefined' ? window.location.href : '',
  className = ''
}: SocialShareProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: description,
          url
        });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setIsOpen(true);
        }
      }
    } else {
      setIsOpen(true);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={handleShare}
        className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm hover:bg-white text-gray-700 rounded-full shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5"
        aria-label="Share"
      >
        <Share2 size={18} />
        <span className="text-sm font-medium">Share</span>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full right-0 mt-2 bg-white rounded-2xl shadow-2xl p-4 z-50 min-w-[240px] animate-scale-in">
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-900 mb-3">Share this page</p>

              <a
                href={shareLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 rounded-xl transition-colors group"
              >
                <Facebook className="text-blue-600 group-hover:scale-110 transition-transform" size={20} />
                <span className="text-sm text-gray-700 group-hover:text-blue-600">Facebook</span>
              </a>

              <a
                href={shareLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 hover:bg-sky-50 rounded-xl transition-colors group"
              >
                <Twitter className="text-sky-500 group-hover:scale-110 transition-transform" size={20} />
                <span className="text-sm text-gray-700 group-hover:text-sky-500">Twitter</span>
              </a>

              <a
                href={shareLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 rounded-xl transition-colors group"
              >
                <Linkedin className="text-blue-700 group-hover:scale-110 transition-transform" size={20} />
                <span className="text-sm text-gray-700 group-hover:text-blue-700">LinkedIn</span>
              </a>

              <button
                onClick={copyToClipboard}
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-xl transition-colors group w-full"
              >
                {copied ? (
                  <>
                    <Check className="text-green-600 group-hover:scale-110 transition-transform" size={20} />
                    <span className="text-sm text-green-600">Copied!</span>
                  </>
                ) : (
                  <>
                    <Link2 className="text-gray-600 group-hover:scale-110 transition-transform" size={20} />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">Copy Link</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
